import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, ChevronUp, X, Trash2, Check, Send, Mail, CheckCircle2, FileText, Building2, ArrowLeftRight, Package, Lock, ArrowLeft } from 'lucide-react';
import { useSelections, Selection } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
type FinalizeStep = 'review' | 'email' | 'otp' | 'processing' | 'success';
interface SelectionsWidgetProps {
  onSwap?: (category: string, itemName: string) => void;
  isGalleryOpen?: boolean;
}
export function SelectionsWidget({
  onSwap,
  isGalleryOpen = false
}: SelectionsWidgetProps) {
  const {
    selections,
    totalPrice,
    recentlyAdded,
    clearRecentlyAdded,
    removeSelection,
    clearSelections,
    activePackage,
    clearActivePackage,
    isItemOverridingPackage,
    getPackageItemPrice,
    packageOverrides,
    resetPackageOverrides,
    getOriginalPackageOption
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [finalizeStep, setFinalizeStep] = useState<FinalizeStep>('review');
  const [confirmClear, setConfirmClear] = useState(false);
  // Auth mode: 'authenticated' skips email/OTP, 'guest' requires it
  const [authMode, setAuthMode] = useState<'authenticated' | 'guest'>('authenticated');
  // Email & OTP State
  const [email, setEmail] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(''));
  const [otpError, setOtpError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Show toast when new item is added
  useEffect(() => {
    if (recentlyAdded) {
      setShowToast(true);
      const timer = setTimeout(() => {
        setShowToast(false);
        clearRecentlyAdded();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [recentlyAdded, clearRecentlyAdded]);
  const handleFinalize = () => {
    if (authMode === 'authenticated') {
      // Known user — skip email/OTP, go straight to processing
      setFinalizeStep('processing');
      setTimeout(() => setFinalizeStep('success'), 2500);
    } else {
      // Guest — need email + OTP
      setFinalizeStep('email');
    }
  };
  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim() && email.includes('@')) {
      setFinalizeStep('otp');
      // Auto-focus first OTP input after short delay for transition
      setTimeout(() => otpRefs.current[0]?.focus(), 100);
    }
  };
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pasted = value.slice(0, 6).split('');
      const newDigits = [...otpDigits];
      pasted.forEach((char, i) => {
        if (index + i < 6) newDigits[index + i] = char;
      });
      setOtpDigits(newDigits);
      setOtpError(false);
      // Focus last filled input or next empty
      const nextIndex = Math.min(index + pasted.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    setOtpError(false);
    // Auto-advance
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (otpDigits.some(d => !d)) {
      setOtpError(true);
      return;
    }
    setIsVerifying(true);
    // Simulate verification
    setTimeout(() => {
      setIsVerifying(false);
      setFinalizeStep('processing');
      // Simulate processing
      setTimeout(() => {
        setFinalizeStep('success');
      }, 2500);
    }, 1000);
  };
  const handleClose = () => {
    setIsExpanded(false);
    // Reset to review step after closing
    setTimeout(() => {
      setFinalizeStep('review');
      setEmail('');
      setOtpDigits(Array(6).fill(''));
      setOtpError(false);
      setConfirmClear(false);
    }, 300);
  };
  const handleStartOver = () => {
    clearSelections();
    setFinalizeStep('review');
    setEmail('');
    setOtpDigits(Array(6).fill(''));
    setOtpError(false);
    setConfirmClear(false);
    setIsExpanded(false);
  };
  const handleSwap = (category: string, itemName: string) => {
    setIsExpanded(false);
    onSwap?.(category, itemName);
  };
  const itemCount = selections.length;
  if (itemCount === 0 && !activePackage && !showToast && finalizeStep === 'review') {
    return null;
  }
  return <>
      {/* Toast Notification */}
      <AnimatePresence data-id="element-1808">
        {showToast && recentlyAdded && <motion.div initial={{
        opacity: 0,
        y: 20,
        x: 20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed bottom-[140px] right-6 z-[100] pointer-events-none" data-id="element-1809">
            <div className="flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-2xl border bg-slate-900/80 border-white/10 text-white backdrop-blur-xl" data-id="element-1810">
              <div className="p-1.5 rounded-full bg-emerald-500" data-id="element-1811">
                <Check className="w-4 h-4 text-white" data-id="element-1812" />
              </div>
              <div className="flex flex-col" data-id="element-1813">
                <span className="text-sm font-semibold text-white" data-id="element-1814">
                  Added to selections
                </span>
                <span className="text-xs text-white/60" data-id="element-1815">
                  {recentlyAdded.itemName} - {recentlyAdded.optionName}
                </span>
              </div>
              <span className="text-sm font-bold ml-2 text-emerald-400" data-id="element-1816">
                {recentlyAdded.price === 0 ? 'Included' : `+${recentlyAdded.price.toLocaleString()}`}
              </span>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Collapsed Widget */}
      <AnimatePresence data-id="element-1817">
        {!isExpanded && itemCount > 0 && <motion.button initial={{
        scale: 0.8,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.8,
        opacity: 0
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} onClick={() => setIsExpanded(true)} className={`fixed right-6 z-[90] flex items-center gap-3 px-4 py-3 rounded-2xl shadow-2xl backdrop-blur-xl border transition-all duration-300 ${isDark ? 'bg-slate-900/80 border-cyan-500/30 hover:border-cyan-500/50' : 'bg-white/80 border-teal-500/30 hover:border-teal-500/50'} ${isGalleryOpen ? 'bottom-[360px]' : 'bottom-[80px]'}`} data-id="element-1818">
            <div className={`relative p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1819">
              <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1820" />
              <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${isDark ? 'bg-cyan-500 text-white' : 'bg-teal-600 text-white'}`} data-id="element-1821">
                {itemCount}
              </div>
            </div>
            <div className="flex flex-col items-start" data-id="element-1822">
              <span className={`text-xs font-medium ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1823">
                Your Selections
              </span>
              <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1824">
                ${totalPrice.toLocaleString()}
              </span>
            </div>
            <ChevronUp className={`w-4 h-4 ml-2 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1825" />
          </motion.button>}
      </AnimatePresence>

      {/* Expanded Panel */}
      <AnimatePresence data-id="element-1826">
        {isExpanded && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={finalizeStep === 'review' ? handleClose : undefined} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[95]" data-id="element-1827" />

            {/* Panel - Centered modal instead of bottom sheet */}
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className={`fixed top-8 bottom-8 right-6 w-full max-w-md z-[100] rounded-2xl shadow-2xl backdrop-blur-xl border flex flex-col overflow-hidden ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} data-id="element-1828">
              {/* Review Step */}
              {finalizeStep === 'review' && <>
                  {/* Header */}
                  <div className={`flex items-center justify-between p-5 border-b flex-shrink-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1829">
                    <div className="flex items-center gap-3" data-id="element-1830">
                      <div className={`p-2 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1831">
                        <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1832" />
                      </div>
                      <div data-id="element-1833">
                        <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1834">
                          Your Selections
                        </h2>
                        <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1835">
                          {itemCount} upgrade{itemCount !== 1 ? 's' : ''}{' '}
                          selected
                        </p>
                      </div>
                    </div>
                    <button onClick={handleClose} className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/60 hover:text-white' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`} data-id="element-1836">
                      <X className="w-5 h-5" data-id="element-1837" />
                    </button>
                  </div>

                  {/* Selections List */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0" data-id="element-1838">
                    {/* Active Package Group */}
                    {activePackage && <div className={`rounded-xl border overflow-hidden ${isDark ? 'border-cyan-500/30 bg-cyan-500/5' : 'border-teal-500/30 bg-teal-50/50'}`} data-id="element-1839">
                        {/* Package Header */}
                        <div className={`flex items-center gap-3 p-4 ${isDark ? 'bg-cyan-500/10' : 'bg-teal-50'}`} data-id="element-1840">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1841">
                            <Package className={`w-5 h-5 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1842" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-1843">
                            <p className={`text-xs font-medium uppercase tracking-wider ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-1844">
                              Kitchen Package
                            </p>
                            <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1845">
                              {activePackage.tier} Package
                            </p>
                          </div>
                          {activePackage.price !== undefined && activePackage.price > 0 && <span className={`text-lg font-bold flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1846">
                                ${activePackage.price.toLocaleString()}
                              </span>}
                          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1847">
                            {onSwap && <button onClick={() => {
                      setIsExpanded(false);
                      onSwap?.('Kitchen', 'Kitchen Packages');
                    }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Package" data-id="element-1848">
                                <ArrowLeftRight className="w-4 h-4" data-id="element-1849" />
                              </button>}
                            <button onClick={clearActivePackage} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Package" data-id="element-1850">
                              <X className="w-4 h-4" data-id="element-1851" />
                            </button>
                          </div>
                        </div>
                        {/* Reset to Package button when overrides exist */}
                        {packageOverrides.length > 0 && <div className={`mx-4 my-3 flex items-center gap-2`} data-id="element-1852">
                            <span className={`text-[10px] font-medium ${isDark ? 'text-amber-400/70' : 'text-amber-600/70'}`} data-id="element-1853">
                              {packageOverrides.length} item
                              {packageOverrides.length !== 1 ? 's' : ''}{' '}
                              modified
                            </span>
                            <button onClick={resetPackageOverrides} className={`ml-auto text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg transition-colors ${isDark ? 'text-amber-400 bg-amber-500/10 hover:bg-amber-500/20' : 'text-amber-600 bg-amber-50 hover:bg-amber-100'}`} data-id="element-1854">
                              Reset to Package
                            </button>
                          </div>}

                        {/* Package Items (nested) */}
                        <div className="p-2 space-y-1" data-id="element-1855">
                          {selections.filter(s => s.isPackageItem || isItemOverridingPackage(s.itemName)).map(selection => {
                    const isOverride = isItemOverridingPackage(selection.itemName);
                    const originalPrice = getPackageItemPrice(selection.itemName);
                    const delta = isOverride && originalPrice !== undefined ? selection.price - originalPrice : 0;
                    return <div key={selection.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${isOverride ? isDark ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-amber-50 border border-amber-200/50' : isDark ? 'bg-white/5' : 'bg-white/80'}`} data-id="element-1856">
                                  {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" data-id="element-1857" /> : <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-1858">
                                      <Lock className={`w-3 h-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1859" />
                                    </div>}
                                  <div className="flex-1 min-w-0" data-id="element-1860">
                                    <div className="flex items-center gap-1.5" data-id="element-1861">
                                      <p className={`text-xs font-medium truncate ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1862">
                                        {selection.itemName}
                                      </p>
                                      {isOverride && <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`} data-id="element-1863">
                                          Upgraded
                                        </span>}
                                    </div>
                                    {isOverride && (() => {
                          const original = getOriginalPackageOption(selection.itemName);
                          return original ? <p className={`text-[10px] truncate line-through ${isDark ? 'text-white/25' : 'text-gray-300'}`} data-id="element-1864">
                                            {original.optionName}
                                          </p> : null;
                        })()}
                                    <p className={`text-[10px] truncate ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1865">
                                      {selection.optionName}
                                    </p>
                                  </div>
                                  {isOverride && delta > 0 && <span className={`text-xs font-bold flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} data-id="element-1866">
                                      +${delta.toLocaleString()}
                                    </span>}
                                </div>;
                  })}
                        </div>
                      </div>}

                    {/* Non-package selections (excluding package overrides) */}
                    {selections.filter(s => !s.isPackageItem && !isItemOverridingPackage(s.itemName)).map((selection, index) => <SelectionCard key={selection.id} selection={selection} index={index} onRemove={() => removeSelection(selection.id)} onSwap={() => handleSwap(selection.category, selection.itemName)} isDark={isDark} data-id="element-1867" />)}

                    {selections.length === 0 && <div className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`} data-id="element-1868">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-50" data-id="element-1869" />
                        <p data-id="element-1870">No selections yet</p>
                        <p className="text-sm mt-1" data-id="element-1871">
                          Browse options to add upgrades
                        </p>
                      </div>}
                  </div>

                  {/* Footer */}
                  {selections.length > 0 && <div className={`p-5 border-t flex-shrink-0 ${isDark ? 'border-white/10 bg-slate-900/50' : 'border-gray-200 bg-gray-50/50'}`} data-id="element-1872">
                      <div className="flex items-center justify-between mb-4" data-id="element-1873">
                        <div data-id="element-1874">
                          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1875">
                            Total Upgrade Value
                          </p>
                          <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1876">
                            ${totalPrice.toLocaleString()}
                          </p>
                        </div>
                        {confirmClear ? <div className="flex items-center gap-2" data-id="element-1877">
                            <span className={`text-xs font-medium ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-1878">
                              Clear all?
                            </span>
                            <button onClick={() => {
                    clearSelections();
                    setConfirmClear(false);
                  }} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-50 text-red-500 hover:bg-red-100'}`} data-id="element-1879">
                              Yes, clear
                            </button>
                            <button onClick={() => setConfirmClear(false)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? 'text-white/50 hover:bg-white/10' : 'text-gray-500 hover:bg-gray-100'}`} data-id="element-1880">
                              Cancel
                            </button>
                          </div> : <button onClick={() => setConfirmClear(true)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${isDark ? 'text-red-400 hover:bg-red-500/10' : 'text-red-500 hover:bg-red-50'}`} data-id="element-1881">
                            <Trash2 className="w-4 h-4" data-id="element-1882" />
                            Clear All
                          </button>}
                      </div>
                      <button onClick={handleFinalize} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1883">
                        <Send className="w-5 h-5" data-id="element-1884" />
                        Finalize Selections
                      </button>
                      {/* Auth mode toggle for demo */}
                      <div className={`flex items-center justify-center gap-2 mt-3 pt-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1885">
                        <span className={`text-[10px] uppercase tracking-wider font-medium ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1886">
                          Demo mode:
                        </span>
                        <div className={`flex rounded-lg overflow-hidden border ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1887">
                          <button onClick={() => setAuthMode('authenticated')} className={`px-2.5 py-1 text-[10px] font-bold transition-colors ${authMode === 'authenticated' ? isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-50 text-emerald-600' : isDark ? 'text-white/30 hover:text-white/50' : 'text-gray-400 hover:text-gray-600'}`} data-id="element-1888">
                            Authenticated
                          </button>
                          <button onClick={() => setAuthMode('guest')} className={`px-2.5 py-1 text-[10px] font-bold transition-colors border-l ${authMode === 'guest' ? isDark ? 'bg-amber-500/20 text-amber-400 border-white/10' : 'bg-amber-50 text-amber-600 border-gray-200' : isDark ? 'text-white/30 hover:text-white/50 border-white/10' : 'text-gray-400 hover:text-gray-600 border-gray-200'}`} data-id="element-1889">
                            Guest
                          </button>
                        </div>
                      </div>
                    </div>}
                </>}

              {/* Email Step */}
              {finalizeStep === 'email' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="flex flex-col p-6" data-id="element-1890">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1891">
                    <button onClick={() => setFinalizeStep('review')} className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-1892">
                      <ArrowLeft className="w-5 h-5" data-id="element-1893" />
                    </button>
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1894">
                      Where should we send this?
                    </h2>
                  </div>

                  <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1895">
                    We need your email to send the selection summary and connect
                    your choices to your profile.
                  </p>

                  <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4" data-id="element-1896">
                    <div className={`relative rounded-xl border-2 transition-all ${isDark ? 'border-cyan-400/50 bg-white/[0.12] focus-within:border-cyan-400 focus-within:shadow-[0_0_24px_rgba(6,182,212,0.25)]' : 'border-teal-500/40 bg-white focus-within:border-teal-500 focus-within:shadow-[0_0_24px_rgba(20,184,166,0.2)]'}`} data-id="element-1897">
                      <Mail className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-cyan-400/70' : 'text-teal-500/60'}`} data-id="element-1898" />
                      <input type="email" required autoFocus placeholder="Enter your email address" value={email} onChange={e => setEmail(e.target.value)} className={`w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium transition-colors outline-none bg-transparent ${isDark ? 'text-white placeholder-white/50' : 'text-gray-900 placeholder-gray-400'}`} data-id="element-1899" />
                    </div>
                    <button type="submit" disabled={!email.includes('@')} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${!email.includes('@') ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1900">
                      Continue
                    </button>
                  </form>
                </motion.div>}

              {/* OTP Step */}
              {finalizeStep === 'otp' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="flex flex-col p-6" data-id="element-1901">
                  <div className="flex items-center gap-3 mb-4" data-id="element-1902">
                    <button onClick={() => setFinalizeStep('email')} className={`p-1.5 -ml-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50' : 'hover:bg-gray-100 text-gray-500'}`} data-id="element-1903">
                      <ArrowLeft className="w-5 h-5" data-id="element-1904" />
                    </button>
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1905">
                      Verify your email
                    </h2>
                  </div>

                  <p className={`text-sm mb-6 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1906">
                    We sent a 6-digit code to{' '}
                    <strong className={isDark ? 'text-white' : 'text-gray-900'} data-id="element-1907">
                      {email}
                    </strong>
                  </p>

                  <form onSubmit={handleOtpSubmit} className="flex flex-col gap-6" data-id="element-1908">
                    <div className="flex justify-between gap-2" data-id="element-1909">
                      {otpDigits.map((digit, i) => <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={6} value={digit} onChange={e => handleOtpChange(i, e.target.value)} onKeyDown={e => handleOtpKeyDown(i, e)} className={`w-12 h-14 text-center text-xl font-bold rounded-lg transition-colors outline-none border ${otpError ? 'border-red-500/50 focus:border-red-500' : isDark ? 'bg-white/5 border-white/10 text-white focus:border-cyan-500/50 focus:bg-white/10' : 'bg-white border-gray-200 text-gray-900 focus:border-teal-500/50 focus:ring-4 focus:ring-teal-500/10'}`} data-id="element-1910" />)}
                    </div>
                    {otpError && <p className="text-red-400 text-xs text-center -mt-2" data-id="element-1911">
                        Please enter a valid 6-digit code
                      </p>}

                    <div className="space-y-4" data-id="element-1912">
                      <button type="submit" disabled={isVerifying || otpDigits.some(d => !d)} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-2 ${isVerifying || otpDigits.some(d => !d) ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-cyan-600 hover:bg-cyan-500' : 'bg-teal-600 hover:bg-teal-500'}`} data-id="element-1913">
                        {isVerifying ? <motion.div animate={{
                    rotate: 360
                  }} transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: 'linear'
                  }} className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full" data-id="element-1914" /> : <>
                            <Lock className="w-4 h-4" data-id="element-1915" />
                            Verify & Submit
                          </>}
                      </button>
                      <button type="button" className={`w-full text-xs font-medium transition-colors ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-teal-600 hover:text-teal-700'}`} data-id="element-1916">
                        Resend code
                      </button>
                    </div>
                  </form>
                </motion.div>}

              {/* Processing Step */}
              {finalizeStep === 'processing' && <div className="flex-1 flex flex-col items-center justify-center p-8" data-id="element-1917">
                  <motion.div initial={{
              scale: 0.8,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} className="text-center" data-id="element-1918">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: 'linear'
              }} className={`w-16 h-16 mx-auto mb-6 rounded-full border-4 border-t-transparent ${isDark ? 'border-cyan-500' : 'border-teal-500'}`} data-id="element-1919" />
                    <h2 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1920">
                      Processing Your Selections
                    </h2>
                    <div className="space-y-3" data-id="element-1921">
                      <ProcessingStep icon={<FileText className="w-4 h-4" data-id="element-1923" />} text="Generating selection summary..." isDark={isDark} delay={0} data-id="element-1922" />
                      <ProcessingStep icon={<Building2 className="w-4 h-4" data-id="element-1925" />} text="Sending to Landmark Homes..." isDark={isDark} delay={0.8} data-id="element-1924" />
                      <ProcessingStep icon={<Mail className="w-4 h-4" data-id="element-1927" />} text="Preparing your email confirmation..." isDark={isDark} delay={1.6} data-id="element-1926" />
                    </div>
                  </motion.div>
                </div>}

              {/* Success Step */}
              {finalizeStep === 'success' && <div className="flex flex-col max-h-full overflow-hidden" data-id="element-1928">
                  {/* Scrollable content */}
                  <div className="flex-1 overflow-y-auto p-6" data-id="element-1929">
                    <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} className="text-center" data-id="element-1930">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 15,
                  stiffness: 200,
                  delay: 0.2
                }} className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`} data-id="element-1931">
                        <CheckCircle2 className={`w-10 h-10 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} data-id="element-1932" />
                      </motion.div>

                      <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1933">
                        Selections Submitted!
                      </h2>
                      <p className={`text-sm mb-5 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1934">
                        Your selections have been sent to Landmark Homes
                      </p>

                      {/* Summary Card */}
                      <div className={`rounded-xl p-4 mb-4 text-left ${isDark ? 'bg-slate-800/50 border border-white/10' : 'bg-gray-50 border border-gray-200'}`} data-id="element-1935">
                        <div className="flex items-center justify-between mb-2" data-id="element-1936">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1937">
                            Total Upgrades
                          </span>
                          <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1938">
                            {itemCount} items
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-3" data-id="element-1939">
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-1940">
                            Total Value
                          </span>
                          <span className={`text-lg font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1941">
                            ${totalPrice.toLocaleString()}
                          </span>
                        </div>
                        <div className={`pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-id="element-1942">
                          <div className="flex items-center gap-2 text-sm" data-id="element-1943">
                            <Mail className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1944" />
                            <span className={isDark ? 'text-white/70' : 'text-gray-600'} data-id="element-1945">
                              Confirmation sent to{' '}
                              <span className="font-medium" data-id="element-1946">
                                demo@example.com
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* What's Next */}
                      <div className={`rounded-xl p-4 text-left ${isDark ? 'bg-cyan-500/10 border border-cyan-500/20' : 'bg-teal-50 border border-teal-200'}`} data-id="element-1947">
                        <h3 className={`font-semibold mb-3 ${isDark ? 'text-cyan-400' : 'text-teal-700'}`} data-id="element-1948">
                          What happens next?
                        </h3>
                        <ul className={`text-sm space-y-3 ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1949">
                          <li className="flex gap-3" data-id="element-1950">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1951" />
                            <span data-id="element-1952">
                              A design consultant will review your selections
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-1953">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1954" />
                            <span data-id="element-1955">
                              Receive a detailed quote within 24-48 hours
                            </span>
                          </li>
                          <li className="flex gap-3" data-id="element-1956">
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${isDark ? 'bg-cyan-400' : 'bg-teal-500'}`} data-id="element-1957" />
                            <span data-id="element-1958">
                              Schedule a call to finalize your dream home
                            </span>
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  </div>

                  {/* Fixed Actions at bottom */}
                  <div className={`p-5 border-t flex-shrink-0 space-y-2 ${isDark ? 'border-white/10 bg-slate-900/80' : 'border-gray-200 bg-white/80'}`} data-id="element-1959">
                    <button onClick={handleClose} className={`w-full py-3.5 rounded-xl font-bold text-white transition-all ${isDark ? 'bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400' : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400'}`} data-id="element-1960">
                      Continue Exploring
                    </button>
                    <button onClick={handleStartOver} className={`w-full py-2.5 rounded-xl font-medium transition-all ${isDark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`} data-id="element-1961">
                      Start New Selection
                    </button>
                  </div>
                </div>}
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}
interface ProcessingStepProps {
  icon: React.ReactNode;
  text: string;
  isDark: boolean;
  delay: number;
}
function ProcessingStep({
  icon,
  text,
  isDark,
  delay
}: ProcessingStepProps) {
  return <motion.div initial={{
    opacity: 0,
    x: -10
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay
  }} className={`flex items-center gap-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1962">
      <div className={`p-1.5 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-100'}`} data-id="element-1963">
        {icon}
      </div>
      <span className="text-sm" data-id="element-1964">{text}</span>
    </motion.div>;
}
interface SelectionCardProps {
  selection: Selection;
  index: number;
  onRemove: () => void;
  onSwap?: () => void;
  isDark: boolean;
}
function SelectionCard({
  selection,
  index,
  onRemove,
  onSwap,
  isDark
}: SelectionCardProps) {
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -20
  }} transition={{
    delay: index * 0.05
  }} className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${isDark ? 'bg-slate-800/50 border-white/5 hover:border-white/10' : 'bg-white border-gray-100 hover:border-gray-200 shadow-sm'}`} data-id="element-1965">
      {/* Thumbnail */}
      {selection.thumbnail ? <img src={selection.thumbnail} alt={selection.optionName} className="w-14 h-14 rounded-lg object-cover flex-shrink-0" data-id="element-1966" /> : <div className={`w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`} data-id="element-1967">
          <ShoppingBag className={`w-5 h-5 ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1968" />
        </div>}

      {/* Info */}
      <div className="flex-1 min-w-0" data-id="element-1969">
        <p className={`text-xs font-medium uppercase tracking-wider mb-0.5 ${isDark ? 'text-cyan-400/70' : 'text-teal-600/70'}`} data-id="element-1970">
          {selection.category}
        </p>
        <p className={`font-semibold truncate text-sm ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1971">
          {selection.itemName}
        </p>
        <p className={`text-xs truncate ${isDark ? 'text-white/60' : 'text-gray-500'}`} data-id="element-1972">
          {selection.optionName}
        </p>
      </div>

      {/* Price & Actions */}
      <div className="flex flex-col items-end gap-1 flex-shrink-0" data-id="element-1973">
        <span className={`text-base font-bold ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1974">
          ${selection.price.toLocaleString()}
        </span>
        <div className="flex items-center gap-1" data-id="element-1975">
          {onSwap && <button onClick={onSwap} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-cyan-400 hover:bg-cyan-500/10' : 'text-gray-400 hover:text-teal-500 hover:bg-teal-50'}`} title="Swap Item" data-id="element-1976">
              <ArrowLeftRight className="w-4 h-4" data-id="element-1977" />
            </button>}
          <button onClick={onRemove} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-white/40 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-400 hover:text-red-500 hover:bg-red-50'}`} title="Remove Item" data-id="element-1978">
            <X className="w-4 h-4" data-id="element-1979" />
          </button>
        </div>
      </div>
    </motion.div>;
}